import React, { useState } from "react";
import { View, TouchableOpacity, StyleSheet, Text } from "react-native";
import { observer } from "mobx-react";
import { Ionicons } from "@expo/vector-icons";
import Tooltip from "../../Components/Tooltip";
import Colors from "../../../Resources/Color";
import { BlockPlanificationConstants } from "../../../Resources/Constants";
import MediaManager from "../../Classes/MediaManager";
import { useDevice } from "../../_layout";
import FloatingMenu from "../../Components/FloatingMenu";
import GlobalStyles from "../../../Resources/Styles";
import { useTranslation } from "react-i18next";

export const CameraIconView = observer(
  ({ serie, block, isEditMode, persistBlockChange }) => {
    const { isMobile } = useDevice();
    const [isCameraHovered, setIsCameraHovered] = useState(false);

    // Mobile-specific dimensions
    const columnWidth = isMobile ? 20 : 24;
    const pillHeight = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
      : BlockPlanificationConstants.VALUE_PILL_HEIGHT;
    const iconSize = isMobile ? 16 : 16;

    async function handleVideoPress() {
      if (!serie || serie.id === "placeholder") return;

      if (serie.hasVideo) {
        const coach = block?.client?.coach ?? block?.coach ?? null;
        if (coach && !coach.isOffline) {
          const mediaManager = MediaManager.getInstance();
          mediaManager.showMediaPlayer(undefined, serie);
        }
      } else {
        if (serie.requireVideo) {
          serie.setProperty("requireVideo", false);
          persistBlockChange();
        } else {
          serie.setProperty("requireVideo", true);
          persistBlockChange();
        }
      }
    }

    const getIconName = () => {
      if (serie?.hasVideo) return "videocam";
      if (serie?.requireVideo) return "videocam-outline";
      return "videocam-off-outline";
    };

    const getIconColor = () => {
      if (serie?.hasVideo || serie?.requireVideo) {
        return Colors.darkgrayColor;
      }
      return "rgba(0, 0, 0, 0.2)";
    };

    const getTooltipContent = () => {
      if (serie?.hasVideo) return "";
      if (serie?.requireVideo) return "dontRequestVideo-string";
      return "requestVideo-string";
    };

    const shouldShowIcon = isEditMode || serie?.requireVideo || serie?.hasVideo;

    if (!shouldShowIcon || !serie || serie.id === "placeholder") {
      return (
        <View
          style={[
            styles.setRowCameraColumn,
            { width: columnWidth, height: pillHeight },
          ]}
        />
      );
    }

    return (
      <View
        style={[
          styles.setRowCameraColumn,
          { width: columnWidth, height: pillHeight },
        ]}
      >
        <Tooltip
          content={getTooltipContent()}
          placement="top"
          delayedOpen={true}
          disabled={serie.hasVideo && !isEditMode}
        >
          <TouchableOpacity
            style={{
              opacity: isCameraHovered ? 0.5 : 1,
              padding: 2,
            }}
            onPress={handleVideoPress}
            onMouseEnter={() => !isMobile && setIsCameraHovered(true)}
            onMouseLeave={() => setIsCameraHovered(false)}
          >
            <Ionicons
              name={getIconName()}
              size={iconSize}
              color={getIconColor()}
            />
          </TouchableOpacity>
        </Tooltip>
      </View>
    );
  },
);

export const NonEditVideoDot = observer(({ serie }) => {
  const { isMobile } = useDevice();
  const columnWidth = isMobile ? 20 : 24;
  const pillHeight = isMobile
    ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
    : BlockPlanificationConstants.VALUE_PILL_HEIGHT;
  const dotSize = isMobile ? 7 : 8;

  const hasVideo = !!serie?.hasVideo;
  const requireVideo = !!serie?.requireVideo;

  const dotStyle = hasVideo
    ? [
        styles.videoDotFilled,
        { width: dotSize, height: dotSize, borderRadius: dotSize / 2 },
      ]
    : requireVideo
      ? [
          styles.videoDotOutline,
          { width: dotSize, height: dotSize, borderRadius: dotSize / 2 },
        ]
      : [
          styles.videoDotNone,
          { width: dotSize, height: dotSize, borderRadius: dotSize / 2 },
        ];

  return (
    <View
      style={[
        styles.setRowCameraColumn,
        { width: columnWidth, height: pillHeight },
      ]}
    >
      <View style={dotStyle} />
    </View>
  );
});

const styles = StyleSheet.create({
  setRowCameraColumn: {
    alignItems: "center",
    justifyContent: "center",
  },
  videoDotBase: {},
  videoDotFilled: {
    backgroundColor: Colors.darkgrayColor,
  },
  videoDotOutline: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: Colors.darkgrayColor,
  },
  videoDotNone: {
    backgroundColor: "transparent",
  },
  setRowPriorityColumn: {
    alignItems: "center",
    justifyContent: "center",
  },
  priorityDotSmall: {},
});

const PriorityMenuItem = ({ item, onPress, close, t, isMobile }) => {
  return (
    <TouchableOpacity
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 10,
        paddingHorizontal: 10,
        gap: 8,
      }}
      onPress={() => {
        onPress(item.value);
        close();
      }}
    >
      <View
        style={[
          GlobalStyles.circle,
          {
            backgroundColor: item.color,
          },
        ]}
      />
      <Text
        style={{
          fontSize: 13,
          color: Colors.blackColor,
          fontWeight: "normal",
        }}
      >
        {t(item.label)}
      </Text>
    </TouchableOpacity>
  );
};

export const PriorityIconView = observer(
  ({ serie, block, isEditMode, persistBlockChange }) => {
    const { isMobile } = useDevice();
    const { t } = useTranslation();
    const [isPriorityHovered, setIsPriorityHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Mobile-specific dimensions
    const columnWidth = isMobile ? 20 : 24;
    const pillHeight = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
      : BlockPlanificationConstants.VALUE_PILL_HEIGHT;

    const handleMenuItemPress = (relevance) => {
      if (!serie || serie.id === "placeholder") return;
      serie.setProperty("relevance", relevance);
      persistBlockChange();
    };

    const getPriorityColor = () => {
      if (serie?.relevance === 1) return "red";
      if (serie?.relevance === 2) return "#FFC107"; // yellow for better visibility on light gray
      if (serie?.relevance === 3) return "green";
      return "rgba(0, 0, 0, 0.2)"; // gray/transparent for default
    };

    const menuItems = [
      {
        value: 1,
        label: "highPriority-string",
        color: "red",
      },
      {
        value: 2,
        label: "mediumPriority-string",
        color: "#FFC107", // yellow for better visibility on light gray
      },
      {
        value: 3,
        label: "lowPriority-string",
        color: "green",
      },
    ];

    if (!serie || serie.id === "placeholder") {
      return (
        <View
          style={[
            styles.setRowPriorityColumn,
            { width: columnWidth, height: pillHeight },
          ]}
        />
      );
    }

    return (
      <View
        style={[
          styles.setRowPriorityColumn,
          { width: columnWidth, height: pillHeight },
        ]}
      >
        <FloatingMenu
          items={menuItems}
          onOpenChange={setIsMenuOpen}
          renderItem={({ item, close }) => (
            <PriorityMenuItem
              item={item}
              onPress={handleMenuItemPress}
              close={close}
              t={t}
              isMobile={isMobile}
            />
          )}
          trigger={
            <Tooltip
              content="setPriority-string"
              placement="top"
              delayedOpen={true}
              disabled={isMenuOpen}
            >
              <View
                style={{
                  opacity: isPriorityHovered ? 0.5 : 1,
                  padding: isMobile ? 4 : 5,
                }}
                onMouseEnter={() => !isMobile && setIsPriorityHovered(true)}
                onMouseLeave={() => setIsPriorityHovered(false)}
              >
                <View
                  style={[
                    GlobalStyles.circle,
                    {
                      backgroundColor: getPriorityColor(),
                      marginRight: 1,
                    },
                  ]}
                />
              </View>
            </Tooltip>
          }
        />
      </View>
    );
  },
);

export const NonEditPriorityDot = observer(({ serie }) => {
  const { isMobile } = useDevice();
  const columnWidth = isMobile ? 20 : 24;
  const pillHeight = isMobile
    ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
    : BlockPlanificationConstants.VALUE_PILL_HEIGHT;
  const dotSize = isMobile ? 7 : 8;

  if (!serie || serie.id === "placeholder") {
    return (
      <View
        style={[
          styles.setRowPriorityColumn,
          { width: columnWidth, height: pillHeight },
        ]}
      />
    );
  }

  const relevance = serie?.relevance;
  if (relevance !== 1 && relevance !== 2 && relevance !== 3) {
    return (
      <View
        style={[
          styles.setRowPriorityColumn,
          { width: columnWidth, height: pillHeight },
        ]}
      />
    );
  }

  const getPriorityColor = () => {
    if (relevance === 1) return "red";
    if (relevance === 2) return "#FFC107"; // yellow for better visibility on light gray
    if (relevance === 3) return Colors.lightGrayColor; // low priority: use lightGrayColor to hide it
    return "transparent";
  };

  return (
    <View
      style={[
        styles.setRowPriorityColumn,
        { width: columnWidth, height: pillHeight },
      ]}
    >
      <View
        style={[
          styles.priorityDotSmall,
          {
            backgroundColor: getPriorityColor(),
            width: dotSize,
            height: dotSize,
            borderRadius: dotSize / 2,
          },
        ]}
      />
    </View>
  );
});
